import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL + '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { response } = error;

    if (response) {
      const { status } = response;

      console.error(`❌ HTTP ${status} Error:`, response.data);

      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.error('🔒 Unauthorized - token expired or invalid, clearing session');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          // Redirect to login page
          window.location.href = '/login';
          break;

        case 403:
          // Forbidden - insufficient permissions
          console.error('🚫 Forbidden - insufficient permissions');
          break;

        case 404:
          // Not found
          console.error('🔍 Resource not found');
          break;

        case 500:
          // Server error
          console.error('💥 Server error occurred');
          break;

        default:
          console.error('⚠️ Error:', response.data?.message || 'Unknown error');
          break;
      }
    } else if (error.code === 'ECONNABORTED') {
      console.error('⏱️ Request timeout - please check your connection');
    } else if (error.message === 'Network Error') {
      console.error('🌐 Network error - cannot reach server');
    } else {
      console.error('❓ Unknown error:', error.message);
    }

    return Promise.reject(error);
  }
);

// API service class
class AdminApiService {
  // Authentication
  static async login(email, password) {
    try {
      console.log('🔐 Attempting admin login for:', email);

      const response = await api.post('/auth/login', { email, password });

      console.log('✅ Login response received:', response.data);

      if (response.data.success && response.data.data) {
        const { token, user } = response.data.data;

        console.log('👤 User role:', user.role);

        // Only allow admins to login
        // Managers and super admins use the dashboard too. What each is
        // allowed to do is enforced by the API, not here.
        const STAFF_ROLES = ['admin', 'super_admin', 'manager'];
        const signedInRoles = Array.isArray(user.roles) && user.roles.length
          ? user.roles
          : [user.role];

        if (!signedInRoles.some((r) => STAFF_ROLES.includes(r))) {
          console.error('❌ Invalid role - not an admin');
          return {
            success: false,
            message: 'Access denied. Admin privileges required.'
          };
        }

        localStorage.setItem('adminToken', token);
        localStorage.setItem('adminUser', JSON.stringify(user));

        console.log('✅ Admin login successful, token saved');
        return { success: true, user, token };
      }

      console.error('❌ Login failed:', response.data.message);
      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error) {
      console.error('❌ Login error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMessage = 'Login failed';

      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timeout. Please check your internet connection.';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Cannot connect to server. Please check:\n1. Backend server is running\n2. API URL is correct\n3. Your internet connection';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  static logout() {
    try {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      console.log('✅ Admin logout successful');
      window.location.href = '/login';
      return { success: true };
    } catch (error) {
      console.error('❌ Logout error:', error);
      return { success: false, message: 'Logout failed' };
    }
  }

  // Agent Management
  static async getAgents() {
    try {
      const response = await api.get('/admin/agents');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching agents:', error);
      throw error;
    }
  }

  static async getAgentById(id) {
    try {
      const response = await api.get(`/admin/agents/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching agent details:', error);
      throw error;
    }
  }

  static async createAgent(agentData) {
    try {
      const response = await api.post('/admin/agents', agentData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating agent:', error);
      throw error;
    }
  }

  static async updateAgent(id, agentData) {
    try {
      const response = await api.put(`/admin/agents/${id}`, agentData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating agent:', error);
      throw error;
    }
  }

  static async blockAgent(id) {
    try {
      const response = await api.patch(`/admin/agents/${id}/block`);
      return response.data;
    } catch (error) {
      console.error('❌ Error blocking agent:', error);
      throw error;
    }
  }

  static async unblockAgent(id) {
    try {
      const response = await api.patch(`/admin/agents/${id}/unblock`);
      return response.data;
    } catch (error) {
      console.error('❌ Error unblocking agent:', error);
      throw error;
    }
  }

  static async deleteAgent(id) {
    try {
      const response = await api.delete(`/admin/agents/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting agent:', error);
      throw error;
    }
  }

  // Car Management
  static async getCars() {
    try {
      const response = await api.get('/admin/cars');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching cars:', error);
      throw error;
    }
  }

  static async getCarById(id) {
    try {
      const response = await api.get(`/admin/cars/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching car details:', error);
      throw error;
    }
  }

  static async blockCar(id) {
    try {
      const response = await api.patch(`/admin/cars/${id}/block`);
      return response.data;
    } catch (error) {
      console.error('❌ Error blocking car:', error);
      throw error;
    }
  }

  static async unblockCar(id) {
    try {
      const response = await api.patch(`/admin/cars/${id}/unblock`);
      return response.data;
    } catch (error) {
      console.error('❌ Error unblocking car:', error);
      throw error;
    }
  }

  static async deleteCar(id) {
    try {
      const response = await api.delete(`/admin/cars/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting car:', error);
      throw error;
    }
  }

  // Seller Management
  static async getSellers(params = {}) {
    try {
      const response = await api.get('/admin/sellers', { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching sellers:', error);
      throw error;
    }
  }

  static async getSellerById(id) {
    try {
      const response = await api.get(`/admin/sellers/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching seller details:', error);
      throw error;
    }
  }

  static async createSeller(sellerData) {
    try {
      const response = await api.post('/admin/sellers', sellerData);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating seller:', error);
      throw error;
    }
  }

  static async updateSeller(id, sellerData) {
    try {
      const response = await api.put(`/admin/sellers/${id}`, sellerData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating seller:', error);
      throw error;
    }
  }

  static async blockSeller(id) {
    try {
      const response = await api.patch(`/admin/sellers/${id}/block`);
      return response.data;
    } catch (error) {
      console.error('❌ Error blocking seller:', error);
      throw error;
    }
  }

  static async unblockSeller(id) {
    try {
      const response = await api.patch(`/admin/sellers/${id}/unblock`);
      return response.data;
    } catch (error) {
      console.error('❌ Error unblocking seller:', error);
      throw error;
    }
  }

  static async deleteSeller(id) {
    try {
      const response = await api.delete(`/admin/sellers/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting seller:', error);
      throw error;
    }
  }

  // Buyer Management
  static async getBuyers() {
    try {
      const response = await api.get('/admin/buyers');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching buyers:', error);
      throw error;
    }
  }

  static async getBuyerById(id) {
    try {
      const response = await api.get(`/admin/buyers/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching buyer details:', error);
      throw error;
    }
  }

  static async blockBuyer(id) {
    try {
      const response = await api.patch(`/admin/buyers/${id}/block`);
      return response.data;
    } catch (error) {
      console.error('❌ Error blocking buyer:', error);
      throw error;
    }
  }

  static async unblockBuyer(id) {
    try {
      const response = await api.patch(`/admin/buyers/${id}/unblock`);
      return response.data;
    } catch (error) {
      console.error('❌ Error unblocking buyer:', error);
      throw error;
    }
  }

  static async deleteBuyer(id) {
    try {
      const response = await api.delete(`/admin/buyers/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting buyer:', error);
      throw error;
    }
  }

  // Membership Management
  static async getMemberships() {
    try {
      const response = await api.get('/admin/memberships');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching memberships:', error);
      throw error;
    }
  }

  static async updateMembership(id, membershipData) {
    try {
      const response = await api.put(`/admin/memberships/${id}`, membershipData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating membership:', error);
      throw error;
    }
  }

  static async getMembershipPlans() {
    try {
      const response = await api.get('/admin/membership-plans');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching membership plans:', error);
      throw error;
    }
  }

  static async updateMembershipPlan(id, planData) {
    try {
      const response = await api.put(`/admin/membership-plans/${id}`, planData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating membership plan:', error);
      throw error;
    }
  }

  // Analytics
  static async getAnalytics() {
    try {
      const response = await api.get('/admin/analytics');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      throw error;
    }
  }

  // App Settings
  static async getAppSettings() {
    try {
      const response = await api.get('/admin/settings');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching app settings:', error);
      throw error;
    }
  }

  static async updateAppSettings(settings) {
    try {
      const response = await api.put('/admin/settings', settings);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating app settings:', error);
      throw error;
    }
  }

  // Car brand & model hierarchy
  static async getCarBrands() {
    try {
      const response = await api.get('/admin/car-brands');
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching car brands:', error);
      throw error;
    }
  }

  static async createCarBrand(payload) {
    try {
      const response = await api.post('/admin/car-brands', payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating car brand:', error);
      throw error;
    }
  }

  static async updateCarBrand(brandId, payload) {
    try {
      const response = await api.patch(`/admin/car-brands/${brandId}`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating car brand:', error);
      throw error;
    }
  }

  static async deleteCarBrand(brandId) {
    try {
      const response = await api.delete(`/admin/car-brands/${brandId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting car brand:', error);
      throw error;
    }
  }

  static async addCarModel(brandId, payload) {
    try {
      const response = await api.post(`/admin/car-brands/${brandId}/models`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error adding car model:', error);
      throw error;
    }
  }

  static async updateCarModel(brandId, modelId, payload) {
    try {
      const response = await api.patch(`/admin/car-brands/${brandId}/models/${modelId}`, payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating car model:', error);
      throw error;
    }
  }

  static async deleteCarModel(brandId, modelId) {
    try {
      const response = await api.delete(`/admin/car-brands/${brandId}/models/${modelId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting car model:', error);
      throw error;
    }
  }

  // Notification broadcasts
  static async getNotificationUsers(params = {}) {
    try {
      const response = await api.get('/admin/notifications/users', { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching notification users:', error);
      throw error;
    }
  }

  static async listBroadcasts(params = {}) {
    try {
      const response = await api.get('/admin/notifications/broadcasts', { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error listing broadcasts:', error);
      throw error;
    }
  }

  static async sendBroadcastNotification(payload) {
    try {
      const response = await api.post('/admin/notifications/broadcast', payload);
      return response.data;
    } catch (error) {
      console.error('❌ Error sending broadcast notification:', error);
      throw error;
    }
  }
}

export { AdminApiService, api };
export default AdminApiService;
